import { useState, type ReactNode } from 'react';
import {
  DataTable,
  EmptyState,
  ErrorState,
  ListChrome,
  PaginationFooter,
  type Column,
  type RowDensity,
  type IconName,
} from '@whinerp/ui';
import { formatCount, formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * One list screen, for every object.
 *
 * Contacts, leads, companies and deals are the same screen with different
 * columns: the toolbar, the density switch, the selection model, the
 * pagination and the four states are identical in each. Written out per object
 * they would drift inside a fortnight - one gains a filter chip, another loses
 * its filtered-to-nothing state, and nobody notices because each screen's own
 * test still passes.
 *
 * What differs per object is passed in. What must never differ is here.
 */

export interface RecordListPage<R> {
  rows: R[];
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
}

export interface FilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

export interface RecordListProps<R> {
  /** The object's plural label, for the title and the table's accessible name. */
  title: string;
  icon: IconName;
  /** What the rows link to, e.g. '/leads'. */
  recordHref: (row: R) => string;
  rowId: (row: R) => string;
  columns: Column<R>[];
  page: RecordListPage<R> | undefined;
  loading: boolean;
  error: { message: string; rule?: string | undefined } | null;
  onRetry: () => void;
  refreshing: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  sort: { field: string; direction: 'asc' | 'desc' };
  onSortChange: (field: string) => void;
  cursorStack: string[];
  onCursorStackChange: (next: string[]) => void;
  pageSize: number;
  /** Rendered between the toolbar and the table: status chips, a score legend. */
  aboveTable?: ReactNode;
  /**
   * The saved-view bar, when this list has one (F-021).
   *
   * A node rather than a set of props, because the bar talks to the views API
   * itself and the caller already holds the state it reads. Absent means this
   * list offers no views - and no bar is drawn, rather than an empty one.
   */
  viewBar?: ReactNode;
  /**
   * Group headings between the rows (F-021).
   *
   * The rows must arrive already in group order: this groups by inserting a
   * heading where the value changes, it does not reorder anything. Regrouping
   * a page here would split a group across a page boundary.
   */
  group?: {
    label: (row: R) => string | null;
    emptyLabel: string;
  };
  filterChips?: FilterChip[];
  /** What an empty list should teach, when it is genuinely empty. */
  emptyTitle: string;
  emptyBody: string;
  emptyIllustration: string;
  /** Absent means this reader cannot create one, so no control is drawn. */
  onNew?: (() => void) | undefined;
  onClearFilters: () => void;
  /** True when a filter or a search is narrowing the list. */
  filtered: boolean;
}

export function RecordList<R>({
  title,
  icon,
  recordHref,
  rowId,
  columns,
  page,
  loading,
  error,
  onRetry,
  refreshing,
  search,
  onSearchChange,
  sort,
  onSortChange,
  cursorStack,
  onCursorStackChange,
  pageSize,
  aboveTable,
  viewBar,
  group,
  filterChips = [],
  emptyTitle,
  emptyBody,
  emptyIllustration,
  onNew,
  onClearFilters,
  filtered,
}: RecordListProps<R>): JSX.Element {
  const t = useT();
  const { session } = useSession();
  const format = useFormat();
  const [density, setDensity] = useState<RowDensity>('comfortable');
  const [inlineEdit, setInlineEdit] = useState(false);
  const [view, setView] = useState<'table' | 'chart'>('table');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [updatedAt] = useState(() => new Date());

  const rows = page?.rows ?? [];

  return (
    <div data-component="RecordList" className="flex w-full flex-col">
    <ListChrome
      labels={{
        searchThisList: t('list.searchThisList'),
        new: t('action.new'),
        import: t('action.import'),
        moreActions: t('list.moreActions'),
        refresh: t('action.refresh'),
        listSettings: t('list.settings'),
        filters: t('list.filters'),
        removeFilter: (filter) => t('list.removeFilter', { values: { filter } }),
        densityComfortable: t('list.density.comfortable'),
        densityCompact: t('list.density.compact'),
        showAsChart: t('list.chartView'),
        showAsTable: t('list.tableView'),
        inlineEdit: t('list.inlineEdit'),
        unsavedChanges: t('list.view.unsaved'),
        save: t('action.save'),
        saveAs: t('action.saveAs'),
        discard: t('action.discard'),
        clearSelection: t('list.clearSelection'),
        bulkToolbar: t('list.bulkToolbar'),
        pinView: (name) => `${t('list.view.pin')}: ${name}`,
        unpinView: (name) => `${t('list.view.unpin')}: ${name}`,
      }}
      icon={icon}
      title={title}
      // No `views` here: the view control is the ViewBar above the table, and
      // a second one drawn with a single option called "All" was a control
      // that could not change anything.
      unsaved={null}
      total={page?.total ?? 0}
      totalLabel={t('list.status.records', {
        values: { count: formatCount(page?.total ?? 0, format) },
      })}
      sortLabel={t('list.status.sortedBy', { values: { field: sort.field } })}
      filterLabel={filtered ? t('list.status.filteredBy', { values: { filters: 'search' } }) : null}
      updatedLabel={t('list.status.updated', {
        values: { when: formatRelativeDate(updatedAt, format) },
      })}
      // No New control unless a caller gives one. A button wired to nothing is
      // indistinguishable from the ones beside it that work.
      {...(onNew ? { onNew } : {})}
      objectActions={[]}
      overflowActions={[]}
      search={search}
      onSearchChange={onSearchChange}
      density={density}
      onDensityChange={setDensity}
      onRefresh={onRetry}
      refreshing={refreshing}
      inlineEdit={inlineEdit}
      onInlineEditChange={setInlineEdit}
      view={view}
      onViewChange={setView}
      filterChips={filterChips}
      selectedCount={selected.size}
      selectedLabel={t('list.selected', { values: { count: formatCount(selected.size, format) } })}
      onClearSelection={() => setSelected(new Set())}
      bulkActions={[]}
      footer={
        <PaginationFooter
          showingLabel={t('list.page.showing', {
            values: {
              from: formatCount(cursorStack.length * pageSize + (rows.length > 0 ? 1 : 0), format),
              to: formatCount(cursorStack.length * pageSize + rows.length, format),
            },
          })}
          previousLabel={t('list.page.previous')}
          nextLabel={t('list.page.next')}
          navigationLabel={t('list.page.next')}
          hasPrevious={cursorStack.length > 0}
          hasNext={page?.hasMore ?? false}
          onPrevious={() => onCursorStackChange(cursorStack.slice(0, -1))}
          onNext={() => {
            const next = page?.nextCursor;
            if (next) onCursorStackChange([...cursorStack, next]);
          }}
        />
      }
    >
      {viewBar}
      {aboveTable}

      {error ? (
        <ErrorState
          title={t('state.error')}
          body={error.message}
          {...(error.rule ? { rule: error.rule } : {})}
          onRetry={onRetry}
          retryLabel={t('action.retry')}
        />
      ) : (
        <DataTable<R>
          mode={session.preferences.density}
          label={title}
          labels={{
            sortBy: (column) => t('list.sortBy', { values: { column } }),
            selectRow: (row) => t('list.selectRow', { values: { row } }),
            rowsShown: (count) => t('list.rowsShown', { values: { count: formatCount(count, format) } }),
          }}
          rows={rows}
          columns={columns}
          {...(group ? { group } : {})}
          rowId={rowId}
          rowHref={recordHref}
          density={density}
          sort={sort}
          onSortChange={onSortChange}
          selection={{
            selected,
            onToggle: (id) =>
              setSelected((current) => {
                const next = new Set(current);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              }),
            onToggleAll: () =>
              setSelected((current) =>
                current.size === rows.length ? new Set() : new Set(rows.map(rowId)),
              ),
            allLabel: t('list.selectAll', { values: { count: formatCount(rows.length, format) } }),
          }}
          loading={loading}
          empty={
            filtered ? (
              // Filtered to nothing is a different state from genuinely empty,
              // and the action it offers is different too.
              <EmptyState
                reason="filtered-to-nothing"
                illustration="MagnifyingGlassMinus"
                title={t('state.filteredToNothing')}
                body={t('state.filteredToNothing.body')}
                primary={{ label: t('action.clearFilters'), onClick: onClearFilters }}
              />
            ) : (
              <EmptyState
                reason="no-records"
                illustration={emptyIllustration}
                title={emptyTitle}
                body={emptyBody}
                {...(onNew ? { primary: { label: t('action.new'), icon: 'Plus', onClick: onNew } } : {})}
                /* Always a way forward, even where this list cannot create.
                   A list somebody cannot add to from here can still be filled
                   from a spreadsheet, and "nothing here" with no door out is
                   where a new workspace stops. */
                secondary={{
                  label: t('empty.action.importContacts'),
                  icon: 'Upload',
                  href: '/setup/import',
                }}
              />
            )
          }
        />
      )}
    </ListChrome>
    </div>
  );
}
